import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useProfile } from '@/hooks/useProfile';
import { usePartner } from '@/hooks/usePartner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { PartnerInvite } from '@/components/portfolio/partner/PartnerInvite';
import { PartnerSettingsPanel } from '@/components/portfolio/partner/PartnerSettingsPanel';
import {
  User,
  Mail,
  Calendar,
  Shield,
  Users,
  Loader2,
  Check,
  UserPlus,
  Heart,
} from 'lucide-react';
import { toast } from 'sonner';
import { RiskTolerance } from '@/types/portfolio';

const RISK_LEVELS: RiskTolerance[] = ['Conservative', 'Moderate', 'Aggressive'];

const relationshipLabels: Record<string, { label: string; icon: typeof Heart }> = {
  spouse: { label: 'Spouse', icon: Heart },
  partner: { label: 'Partner', icon: Users },
  'financial-partner': { label: 'Financial Partner', icon: UserPlus },
};

export function ProfileTab() {
  const { user } = useAuth();
  const { profile, updateProfile, currentAge } = useProfile();
  const { partner, sentInvitations, pendingInvitations, cancelInvitation, refetch } = usePartner();

  // Refetch partner data when component mounts to ensure fresh data
  useEffect(() => {
    refetch();
  }, [refetch]);

  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editedName, setEditedName] = useState(profile?.name || '');
  const [editedDob, setEditedDob] = useState(profile?.date_of_birth || '');
  const [editedRiskTolerance, setEditedRiskTolerance] = useState<RiskTolerance>(
    profile?.risk_tolerance || 'Moderate'
  );

  // Get user avatar and initials
  const avatarUrl = user?.user_metadata?.avatar_url || user?.user_metadata?.picture;
  const userName = profile?.name || user?.user_metadata?.name || user?.user_metadata?.full_name || '';
  const userEmail = user?.email || '';
  const initials = userName
    ? userName
        .split(' ')
        .map((n: string) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2)
    : userEmail[0]?.toUpperCase() || 'U';

  const handleSaveProfile = async () => {
    setIsSaving(true);
    try {
      const success = await updateProfile({
        name: editedName,
        date_of_birth: editedDob || null,
        risk_tolerance: editedRiskTolerance,
      });

      if (success) {
        toast.success('Profile updated successfully');
        setIsEditing(false);
      } else {
        toast.error('Failed to update profile');
      }
    } catch {
      toast.error('An error occurred');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelInvitation = async (invitationId: string) => {
    await cancelInvitation(invitationId);
  };

  // Filter pending sent invitations
  const pendingSentInvitations = sentInvitations.filter((inv) => inv.status === 'pending');

  return (
    <div className="space-y-6 pb-6">
      {/* Profile Header Card */}
      <Card className="relative overflow-hidden">
        {/* Gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-partner/10 pointer-events-none" />

        <CardHeader className="relative pb-4">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <Avatar className="h-16 w-16 border-2 border-background shadow-lg">
                <AvatarImage src={avatarUrl} alt={userName} />
                <AvatarFallback className="bg-primary text-primary-foreground text-lg font-semibold">
                  {initials}
                </AvatarFallback>
              </Avatar>
              <div className="space-y-1">
                <CardTitle className="text-xl">{userName || 'Your Profile'}</CardTitle>
                <CardDescription className="flex items-center gap-1.5">
                  <Mail className="h-3.5 w-3.5" />
                  {userEmail}
                </CardDescription>
              </div>
            </div>
            <Button
              variant={isEditing ? 'default' : 'outline'}
              size="sm"
              onClick={() => {
                if (isEditing) {
                  handleSaveProfile();
                } else {
                  setEditedName(profile?.name || userName);
                  setEditedDob(profile?.date_of_birth || '');
                  setEditedRiskTolerance(profile?.risk_tolerance || 'Moderate');
                  setIsEditing(true);
                }
              }}
              disabled={isSaving}
            >
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : isEditing ? (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Save
                </>
              ) : (
                'Edit Profile'
              )}
            </Button>
          </div>
        </CardHeader>

        <CardContent className="relative space-y-4">
          {isEditing ? (
            <>
              {/* Editable fields */}
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="name" className="flex items-center gap-2">
                    <User className="h-4 w-4 text-muted-foreground" />
                    Full Name
                  </Label>
                  <Input
                    id="name"
                    value={editedName}
                    onChange={(e) => setEditedName(e.target.value)}
                    placeholder="Your name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="dob" className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    Date of Birth
                  </Label>
                  <Input
                    id="dob"
                    type="date"
                    value={editedDob}
                    onChange={(e) => setEditedDob(e.target.value)}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="risk" className="flex items-center gap-2">
                  <Shield className="h-4 w-4 text-muted-foreground" />
                  Risk Tolerance
                </Label>
                <Select
                  value={editedRiskTolerance}
                  onValueChange={(v) => setEditedRiskTolerance(v as RiskTolerance)}
                >
                  <SelectTrigger id="risk">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {RISK_LEVELS.map((level) => (
                      <SelectItem key={level} value={level}>
                        {level}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsEditing(false)}
                  disabled={isSaving}
                >
                  Cancel
                </Button>
              </div>
            </>
          ) : (
            <>
              {/* Display fields */}
              <div className="grid gap-4 sm:grid-cols-3">
                <div className="p-3 rounded-lg bg-muted/50 space-y-1">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Calendar className="h-3.5 w-3.5" />
                    Age
                  </div>
                  <p className="font-medium">
                    {currentAge ? `${currentAge} years old` : 'Not set'}
                  </p>
                </div>
                <div className="p-3 rounded-lg bg-muted/50 space-y-1">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Shield className="h-3.5 w-3.5" />
                    Risk Tolerance
                  </div>
                  <p className="font-medium">{profile?.risk_tolerance || 'Moderate'}</p>
                </div>
                <div className="p-3 rounded-lg bg-muted/50 space-y-1">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Users className="h-3.5 w-3.5" />
                    Partner Status
                  </div>
                  <p className="font-medium">
                    {partner ? (
                      <span className="text-partner">Linked</span>
                    ) : pendingSentInvitations.length > 0 ? (
                      <span className="text-primary">Pending</span>
                    ) : (
                      'Not linked'
                    )}
                  </p>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Linked Partners Section */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="p-1.5 rounded-lg bg-partner/10">
                <Users className="h-4 w-4 text-partner" />
              </div>
              <CardTitle className="text-base">Linked Partners</CardTitle>
            </div>
            {!partner && <PartnerInvite />}
          </div>
          <CardDescription>
            Connect with a spouse or partner to view combined household finances
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {/* Connected Partner */}
          {partner && (
            <div className="p-4 rounded-xl border border-partner/30 bg-partner/5 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-partner/20 flex items-center justify-center">
                    <span className="text-sm font-semibold text-partner">
                      {partner.name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium">{partner.name}</p>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      {(() => {
                        const rel = relationshipLabels[partner.relationshipType];
                        const Icon = rel?.icon || Users;
                        return (
                          <>
                            <Icon className="h-3 w-3" />
                            {rel?.label || partner.relationshipType}
                          </>
                        );
                      })()}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge
                    variant="outline"
                    className="border-partner/30 text-partner bg-partner/10"
                  >
                    Connected
                  </Badge>
                  <PartnerSettingsPanel />
                </div>
              </div>
            </div>
          )}

          {/* Pending Sent Invitations */}
          {pendingSentInvitations.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">
                Pending Invitations
              </p>
              {pendingSentInvitations.map((inv) => (
                <div
                  key={inv.id}
                  className="p-3 rounded-xl border border-border bg-muted/30 flex items-center justify-between"
                >
                  <div className="flex items-center gap-3">
                    <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                      <Mail className="h-4 w-4 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm font-medium">{inv.inviteeEmail}</p>
                      <p className="text-xs text-muted-foreground">
                        Sent{' '}
                        {new Date(inv.createdAt).toLocaleDateString(undefined, {
                          month: 'short',
                          day: 'numeric',
                        })}
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="default"
                    size="sm"
                    onClick={() => handleCancelInvitation(inv.id)}
                  >
                    Cancel
                  </Button>
                </div>
              ))}
            </div>
          )}

          {/* Received Invitations */}
          {pendingInvitations.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">
                Received Invitations
              </p>
              {pendingInvitations.map((inv) => (
                <div
                  key={inv.id}
                  className="p-3 rounded-xl border border-primary/30 bg-primary/5"
                >
                  <p className="text-sm font-medium">
                    You have a pending partner invitation
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Check the notification bell to accept or decline
                  </p>
                </div>
              ))}
            </div>
          )}

          {/* Empty state */}
          {!partner && pendingSentInvitations.length === 0 && pendingInvitations.length === 0 && (
            <div className="text-center py-6 space-y-3">
              <div className="mx-auto w-12 h-12 rounded-full bg-muted/50 flex items-center justify-center">
                <Users className="h-6 w-6 text-muted-foreground" />
              </div>
              <div className="space-y-1">
                <p className="text-sm font-medium">No partner linked yet</p>
                <p className="text-xs text-muted-foreground">
                  Invite your spouse or partner to manage finances together
                </p>
              </div>
              <PartnerInvite
                trigger={
                  <Button variant="outline" size="sm" className="gap-2">
                    <UserPlus className="h-4 w-4" />
                    Invite Partner
                  </Button>
                }
              />
            </div>
          )}
        </CardContent>
      </Card>

    </div>
  );
}
